import { createWebHistory, createRouter } from 'vue-router';
import { useUserStore } from '@/stores/user';
import analytics from '@/services/analytics';
import { getStoredToken } from '@/utils/authToken';
import { PUBLIC_REPORT_ROUTE_NAME, redactPublicReportToken } from '@/utils/publicReportRoute';

// Dynamically import route components from the new views directory
const Main = () => import('@/views/Main.vue');
const Login = () => import('@/views/Login.vue');
const LandingPage = () => import('@/views/LandingPage.vue');
const ExperimentView = () => import('@/views/ExperimentView.vue');
const AdminPanel = () => import('@/views/AdminPanel.vue');
const Settings = () => import('@/views/Settings.vue');
const CustomerDetails = () => import('@/views/admin/CustomerDetails.vue');
const ResetPassword = () => import('@/views/ResetPassword.vue');
const FirstAccess = () => import('@/views/FirstAccess.vue');
const LegalDocumentView = () => import('@/views/LegalDocumentView.vue');
const CompareView = () => import('@/views/CompareView.vue');
const PublicReportView = () => import('@/views/PublicReportView.vue');
const TokenShowcase = () => import('@/design-system/examples/TokenShowcase.vue');

const routes = [
  { path: '/', component: LandingPage, name: 'LandingPage', meta: { requiresAuth: false } },
  { path: '/login', component: Login, name: 'Login' },
  { path: '/reset-password', component: ResetPassword, name: 'ResetPassword', meta: { requiresAuth: false } },
  // SYN-1519: tela de primeiro acesso — o convidado chega por link de e-mail,
  // sem sessão. O `to.name` também entra na allowlist do `authGuard` abaixo.
  { path: '/first-access', component: FirstAccess, name: 'FirstAccess', meta: { requiresAuth: false } },
  // SYN-2721: relatório HTML por link público — o cliente de quem exportou abre
  // sem conta. Também na allowlist do `authGuard` e no `namedRoutesWithoutHeader`.
  { path: '/r/:token', component: PublicReportView, name: PUBLIC_REPORT_ROUTE_NAME, props: true, meta: { requiresAuth: false } },

  // Fixed URLs for Legal Documents
  {
    path: '/terms_of_use',
    component: LegalDocumentView,
    name: 'TermsOfUse',
    props: { docType: 'terms_of_use', titleKey: 'views.legalDocumentView.titles.terms_of_use' },
    meta: { requiresAuth: false }
  },
  {
    path: '/privacy_policy',
    component: LegalDocumentView,
    name: 'PrivacyPolicy',
    props: { docType: 'privacy_policy', titleKey: 'views.legalDocumentView.titles.privacy_policy' },
    meta: { requiresAuth: false }
  },
  {
    path: '/subprocessors_policy',
    component: LegalDocumentView,
    name: 'SubprocessorsPolicy',
    props: { docType: 'subprocessors_policy', titleKey: 'views.legalDocumentView.titles.subprocessors_policy' },
    meta: { requiresAuth: false }
  },

  // Legacy legal documents (Athena) — texto anterior à virada para Idril. Continuam
  // públicos porque os clientes na trilha `legacy` aceitam e consultam estes.
  {
    path: '/terms_of_use_legacy',
    component: LegalDocumentView,
    name: 'TermsOfUseLegacy',
    props: { docType: 'terms_of_use_legacy', titleKey: 'views.legalDocumentView.titles.terms_of_use_legacy' },
    meta: { requiresAuth: false }
  },
  {
    path: '/privacy_policy_legacy',
    component: LegalDocumentView,
    name: 'PrivacyPolicyLegacy',
    props: { docType: 'privacy_policy_legacy', titleKey: 'views.legalDocumentView.titles.privacy_policy_legacy' },
    meta: { requiresAuth: false }
  },
  {
    path: '/subprocessors_policy_legacy',
    component: LegalDocumentView,
    name: 'SubprocessorsPolicyLegacy',
    props: { docType: 'subprocessors_policy_legacy', titleKey: 'views.legalDocumentView.titles.subprocessors_policy_legacy' },
    meta: { requiresAuth: false }
  },

  { path: '/main', component: Main, name: 'MainWithoutFolder', meta: { requiresAuth: true } },
  { path: '/main/:folderId', component: Main, name: 'MainWithFolder', props: true, meta: { requiresAuth: true } },
  {
    path: '/experiment/:experimentId',
    name: 'ExperimentView', 
    component: ExperimentView,
    props: true,
    meta: { requiresAuth: true }
  },
  // SYN-1689: comparação de 2–4 criativos lado a lado. Rota privada (não entra
  // na allowlist pública do authGuard) e de tela cheia — o nome também precisa
  // estar em `namedRoutesWithoutHeader` no App.vue, senão o header global
  // renderiza por cima.
  {
    path: '/compare',
    name: 'CompareView',
    component: CompareView,
    // SYN-1865 — a comparação é feature contratada: sem entitlement a URL
    // direta redireciona em silêncio (o upsell mora no botão, não na URL).
    meta: { requiresAuth: true, features: ['creative_comparison'] }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Settings,
    meta: { requiresAuth: true }
  },
  {
    path: '/admin',
    redirect: '/settings'
  },
  // SYN-1694: o painel deixou de ser exclusivo do superadmin — abre também para
  // o staff que tenha ao menos uma área (`requiresAdminPanel`). Rotas de área
  // específica declaram `meta.areas` (superadmin passa por cima).
  {
    path: '/superadmin',
    name: 'SuperAdminPanel',
    component: AdminPanel,
    meta: { requiresAuth: true, requiresAdminPanel: true }
  },
  {
    path: '/admin/customers/:clientId',
    name: 'CustomerDetails',
    component: CustomerDetails,
    props: true,
    meta: { requiresAuth: true, areas: ['accounts'] }
  },
  {
    path: '/design-system/showcase',
    name: 'TokenShowcase',
    component: TokenShowcase,
    // SYN-2736: era público; restrito ao superadmin. / Was public; superadmin only.
    meta: { requiresAuth: true, roles: ['superadmin'] }
  },
  // Catch-all: qualquer URL desconhecida (ex.: /paineladmin — que NÃO é uma rota
  // real; o painel admin vive em /superadmin, guardado por meta.roles) cai aqui e
  // redireciona pra home em vez de renderizar o shell vazio "acessível". O
  // beforeEach então aplica o requiresAuth do /main (não-autenticado → /login).
  // Sem loop: /main e /login são rotas reais. (SYN-657)
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: { name: 'MainWithoutFolder' }
  }
]

// Extracted as a named export so the scroll rule can be unit-tested directly.
export function scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }
    return { top: 0 }
}

// Navigation guard — extracted as a named export so it can be unit-tested
// without mounting any lazy-loaded view chunk. Behavior is identical to the
// previous inline `router.beforeEach(...)`.
export async function authGuard(to, from, next) {
    const userStore = useUserStore();
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
    const tokenExists = !!getStoredToken();

    // Allow public access to Landing, Login, Reset Password, First Access, Legal Docs and the public report
    if (['LandingPage', 'Login', 'ResetPassword', 'FirstAccess', 'TermsOfUse', 'PrivacyPolicy', 'SubprocessorsPolicy',
         'TermsOfUseLegacy', 'PrivacyPolicyLegacy', 'SubprocessorsPolicyLegacy', PUBLIC_REPORT_ROUTE_NAME].includes(to.name)) {
        if (to.name === 'Login' && userStore.isAuthenticated) {
             return next({ name: 'MainWithoutFolder' });
        }
        return next();
    }

    if (requiresAuth && !tokenExists) {
        console.log(`Route Guard: Denied access to "${to.path}". No token found. Redirecting to /login.`);
        userStore.clearUser();
        return next({ name: 'Login' });
    }

    if (tokenExists && !userStore.isAuthenticated && requiresAuth) {
        console.time("UserValidation");
        try {
            await userStore.fetchAndUpdateUserInfo();
            console.timeEnd("UserValidation");
            if (!userStore.isAuthenticated) {
                 return next({ name: 'Login' });
            }
        } catch (error) {
            console.timeEnd("UserValidation");
            userStore.clearUser();
            return next({ name: 'Login' });
        }
    }

    if (requiresAuth && userStore.isAuthenticated) {
        const requiredRoles = to.meta.roles;
        const userRole = userStore.role;
        if (requiredRoles && Array.isArray(requiredRoles) && !requiredRoles.includes(userRole)) {
            return next({ name: 'MainWithoutFolder' });
        }
        // SYN-1694 — gating por área. Negação segue o padrão atual: redirect
        // silencioso para o /main (snackbar é follow-up, não MVP).
        if (to.meta.requiresAdminPanel && !userStore.canAccessAdminPanel) {
            return next({ name: 'MainWithoutFolder' });
        }
        const requiredAreas = to.meta.areas;
        if (requiredAreas && !userStore.isSuperAdmin && !requiredAreas.some(area => userStore.hasArea(area))) {
            return next({ name: 'MainWithoutFolder' });
        }
        // SYN-1865 — gating por feature contratada (RF3). Mesmo padrão das
        // áreas; o bypass do time interno já mora no `canUseFeature`.
        const requiredFeatures = to.meta.features;
        if (requiredFeatures && !requiredFeatures.some(feature => userStore.canUseFeature(feature))) {
            return next({ name: 'MainWithoutFolder' });
        }
    }

    next();
}

// afterEach page-view tracking — extracted for the same testability reason.
// `to.path`, nunca `to.fullPath`: a query carrega credencial nas rotas de
// convite/reset/SSO (`?token=…`) e R1 só admite ID em path. Em `/r/:token`
// (SYN-2721) a credencial está NO path, então o segmento sai redigido.
export function trackPageView(to) {
    analytics.trackPageView(to.name, redactPublicReportToken(to.path));
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior,
})

router.beforeEach(authGuard);

router.afterEach(trackPageView);

export default router;
